import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { ArrowRightLeft, Database, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Play } from 'lucide-react';

interface MigrationStatus {
  readSource: 'memberships' | 'grants';
  memberships: { total: number };
  grants: { total: number; active: number };
}

export function AccessMigrationPanel() {
  const queryClient = useQueryClient();

  const { data: status, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'access-migration-status'],
    queryFn: async ({ signal }) => {
      const res = await api.get<MigrationStatus>('/api/v1/admin/operations/access-migration-status', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load status');
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: async (source: 'memberships' | 'grants') => {
      const res = await api.put<{ source: string; updated: boolean }>('/api/v1/admin/operations/access-read-source', { source });
      if (!res.success) throw new Error(res.error ?? 'Failed to toggle');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'access-migration-status'] });
    },
  });

  const currentSource = status?.readSource ?? 'memberships';
  const isGrants = currentSource === 'grants';

  const handleToggle = useCallback(() => {
    const newSource = isGrants ? 'memberships' : 'grants';
    if (
      newSource === 'grants' &&
      !window.confirm(
        'Switch reads to access_grants table?\n\nMake sure the backfill has completed successfully before switching. You can switch back at any time.',
      )
    ) {
      return;
    }
    toggleMutation.mutate(newSource);
  }, [isGrants, toggleMutation]);

  const backfillMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ totalMemberships: number; created: number; skipped: number; errors: number; errorDetails?: unknown[] }>(
        '/api/v1/admin/operations/backfill-access-grants',
        { maxRetries: 3 },
      );
      if (!res.success) throw new Error(res.error ?? 'Backfill failed');
      return res.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'access-migration-status'] });
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading migration status…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4" />
          Failed to load migration status
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Access System Migration</span>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Memberships</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{status?.memberships.total ?? '–'}</div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Grants (total)</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{status?.grants.total ?? '–'}</div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Grants (active)</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{status?.grants.active ?? '–'}</div>
        </div>
      </div>

      {/* Backfill Action */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md p-3 mb-3">
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Backfill Grants</div>
          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
            Populate access_grants from store_memberships (idempotent, retries 3×)
          </div>
        </div>
        <button
          type="button"
          onClick={() => backfillMutation.mutate()}
          disabled={backfillMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200 disabled:opacity-50 transition-colors"
        >
          {backfillMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
          {backfillMutation.isPending ? 'Running…' : 'Run Backfill'}
        </button>
      </div>

      {backfillMutation.isSuccess && backfillMutation.data && (
        <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 rounded-md p-2.5 mb-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400 mb-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Backfill complete
          </div>
          <div className="grid grid-cols-4 gap-2 text-[10px]">
            <div><span className="text-gray-500">Total:</span> <span className="font-semibold text-gray-900 dark:text-gray-100">{backfillMutation.data.totalMemberships}</span></div>
            <div><span className="text-gray-500">Created:</span> <span className="font-semibold text-green-700 dark:text-green-400">{backfillMutation.data.created}</span></div>
            <div><span className="text-gray-500">Skipped:</span> <span className="font-semibold text-gray-600 dark:text-gray-400">{backfillMutation.data.skipped}</span></div>
            <div><span className="text-gray-500">Errors:</span> <span className={`font-semibold ${backfillMutation.data.errors > 0 ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>{backfillMutation.data.errors}</span></div>
          </div>
        </div>
      )}

      {backfillMutation.isError && (
        <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 mb-3">
          <AlertTriangle className="w-3 h-3" />
          {backfillMutation.error instanceof Error ? backfillMutation.error.message : 'Backfill failed'}
        </div>
      )}

      {/* Toggle */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md p-3">
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Read Source</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                isGrants
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              <Database className="w-3 h-3" />
              {isGrants ? 'access_grants' : 'store_memberships'}
            </span>
            {isGrants && (
              <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                (new system)
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggle}
          disabled={toggleMutation.isPending}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            isGrants
              ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50'
              : 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
          } disabled:opacity-50`}
        >
          {toggleMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <ArrowRightLeft className="w-3.5 h-3.5" />
          )}
          {isGrants ? 'Switch to Memberships' : 'Switch to Grants'}
        </button>
      </div>

      {toggleMutation.isError && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
          <AlertTriangle className="w-3 h-3" />
          {toggleMutation.error instanceof Error ? toggleMutation.error.message : 'Toggle failed'}
        </div>
      )}

      {toggleMutation.isSuccess && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-3 h-3" />
          Read source updated
        </div>
      )}
    </div>
  );
}
